
import { CreditCard, Smartphone, Apple, User } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { isIOSDevice } from "@/utils/deviceDetection";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const paymentMethods = [
  {
    id: "stripe",
    name: "Credit Card",
    icon: CreditCard
  },
  {
    id: "google-pay",
    name: "Google Pay",
    icon: Smartphone,
    isStripe: true
  },
  {
    id: "apple-pay",
    name: "Apple Pay",
    icon: Apple,
    isStripe: true
  }
];

interface PaymentMethodSelectorProps {
  selectedPaymentMethod: string;
  setSelectedPaymentMethod: (method: string) => void;
}

const PaymentMethodSelector = ({ selectedPaymentMethod, setSelectedPaymentMethod }: PaymentMethodSelectorProps) => {
  // Check if the device is running iOS
  const isIOS = isIOSDevice();
  
  // For iOS devices, show a message and a link to the account page
  if (isIOS) {
    return (
      <div className="text-center py-6">
        <h3 className="text-lg font-semibold mb-4">Subscription Management</h3>
        <p className="mb-6 text-gray-600">
          Due to App Store policies, in-app purchases are not available on iOS devices. 
          Please manage your subscription through your account.
        </p>
        <Link to="/account">
          <Button className="bg-corporate-primary-100 hover:bg-corporate-primary-75">
            Manage Account <User className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  // Show the regular payment method selector for non-iOS devices
  return (
    <div>
      <Label className="text-base font-semibold mb-3 block">
        Select Payment Method
      </Label>
      <RadioGroup defaultValue="stripe" onValueChange={setSelectedPaymentMethod} className="grid grid-cols-2 gap-4">
        {paymentMethods.map(method => {
          const Icon = method.icon;
          return (
            <Label
              key={method.id}
              className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                selectedPaymentMethod === method.id
                  ? "border-[#007BFF] bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value={method.id} />
                <Icon className="h-5 w-5 text-gray-600" />
                <span className="font-medium">{method.name}</span>
              </div>
            </Label>
          );
        })}
      </RadioGroup>
    </div>
  );
};

export default PaymentMethodSelector;
